import { Box, Typography } from "@mui/material";
import { HospitalityListing, ViewTypes } from "../../../types";
import InfoCell from "../../../../common/components/ui-kit/info-cell";
import CheckItem from "../common/check-item";
import {
  isExpiredRange,
  toDateKey,
} from "@/modules/property-tracks/utils/listing";

const formatHour = (hour?: number | null) => {
  if (hour == null) return "N/A";
  const period = hour >= 12 ? "PM" : "AM";
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:00 ${period}`;
};

export default function HospitalityListingDetails({
  listing,
}: {
  listing: HospitalityListing;
}) {
  const upcomingExceptionalPrices = (listing.exceptionalPrices ?? [])
    .map((range) => ({
      ...range,
      fromDate: toDateKey(range.fromDate),
      toDate: toDateKey(range.toDate),
    }))
    .filter((range) => !isExpiredRange(range));

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
      })}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Listing Info
          </Typography>
          <Box
            sx={(t) => ({
              display: "flex",
              flexWrap: "wrap",
              gap: t.nawy.spacing["2xl"],
            })}
          >
            <InfoCell title="Name" description={listing.name} />
            <InfoCell
              title="Name (Arabic)"
              description={listing.nameAr ?? "N/A"}
            />
            <InfoCell
              title="Description"
              description={listing.description ?? "N/A"}
            />
            <InfoCell
              title="Special Instructions"
              description={listing.specialInstruction ?? "N/A"}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Pricing
          </Typography>
          <Box
            sx={(t) => ({
              display: "flex",
              flexWrap: "wrap",
              gap: t.nawy.spacing["2xl"],
            })}
          >
            <InfoCell
              title="Price"
              description={`${listing.price.toLocaleString()} ${listing.currencyCode}`}
            />
            <InfoCell
              title="Extra Person Price"
              description={
                listing.priceForExtraPerson != null
                  ? `${listing.priceForExtraPerson.toLocaleString()} ${listing.currencyCode}`
                  : "N/A"
              }
            />
            <InfoCell
              title="Airbnb Markup"
              description={
                listing.airbnbOfficialListingMarkup != null
                  ? `${listing.airbnbOfficialListingMarkup}%`
                  : "N/A"
              }
            />
            <InfoCell
              title="Booking Engine Markup"
              description={
                listing.bookingEngineMarkup != null
                  ? `${listing.bookingEngineMarkup}%`
                  : "N/A"
              }
            />
          </Box>
          {upcomingExceptionalPrices.length > 0 && (
            <Box
              sx={(t) => ({
                display: "flex",
                flexWrap: "wrap",
                gap: t.nawy.spacing["2xl"],
              })}
            >
              {upcomingExceptionalPrices.map((range) => (
                <InfoCell
                  key={range.uid}
                  title={`${range.fromDate} → ${range.toDate}`}
                  description={`${range.price.toLocaleString()} ${listing.currencyCode}`}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Stay Details
          </Typography>
          <Box
            sx={(t) => ({
              display: "flex",
              flexWrap: "wrap",
              gap: t.nawy.spacing["2xl"],
            })}
          >
            <InfoCell
              title="Check-in Start"
              description={formatHour(listing.checkInTimeStart)}
            />
            <InfoCell
              title="Check-in End"
              description={formatHour(listing.checkInTimeEnd)}
            />
            <InfoCell
              title="Check-out"
              description={formatHour(listing.checkOutTime)}
            />
            <InfoCell
              title="Min Nights"
              description={listing.minNights?.toString() ?? "N/A"}
            />
            <InfoCell
              title="Max Nights"
              description={listing.maxNights?.toString() ?? "N/A"}
            />
          </Box>
        </Box>

        <Box
          sx={(t) => ({
            display: "flex",
            alignItems: "center",
            gap: t.nawy.spacing.xl,
          })}
        >
          <CheckItem text="Featured" isTrue={listing.isFeatured ?? false} />
        </Box>
      </Box>

      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Room Details
          </Typography>
          <Box
            sx={(t) => ({
              display: "flex",
              flexWrap: "wrap",
              gap: t.nawy.spacing["2xl"],
            })}
          >
            <InfoCell
              title="Max Guests"
              description={listing.maxNumberOfGuests?.toString() ?? "N/A"}
            />
            <InfoCell
              title="Bed Count"
              description={listing.bedCount?.toString() ?? "N/A"}
            />
            <InfoCell
              title="View Type"
              description={
                listing.viewType
                  ? ViewTypes[listing.viewType as keyof typeof ViewTypes] ?? listing.viewType
                  : "N/A"
              }
            />
            <InfoCell
              title="Address"
              description={listing.address ?? "N/A"}
            />
          </Box>
        </Box>

        <Box
          sx={(t) => ({
            display: "flex",
            alignItems: "center",
            gap: t.nawy.spacing.xl,
          })}
        >
          <CheckItem text="Extra Bed Allowed" isTrue={listing.isExtraBedAllowed ?? false} />
          <CheckItem text="Smoking Allowed" isTrue={listing.isSmokingAllowed ?? false} />
          <CheckItem text="Pets Allowed" isTrue={listing.isPetsAllowed ?? false} />
          <CheckItem text="Parties Allowed" isTrue={listing.isPartiesAllowed ?? false} />
        </Box>
      </Box>
    </Box>
  );
}
